import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ATTRIBUTES = {
  EXPANDED: 'expanded',
  ANIMATING: 'animating',
  CLAMPED: 'clamped',
  FITS: 'fits',
};

const PROPERTIES = {
  CLAMP_HEIGHT: '--clamp-height',
  LINE_TOP: '--clamp-line-top',
  LINE_HEIGHT: '--clamp-line-height',
  DOTS_START: '--clamp-dots-start',
};

const MEASURE_DEBOUNCE = 150;

/** The CSS transition is 300ms; the rest is slack for the event to arrive. */
const TRANSITION_TIMEOUT = 400;

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';

/**
 * @typedef {Object} Line
 * @property {number} top
 * @property {number} bottom
 * @property {number[]} rights Right edge of every word on the line.
 * @property {boolean} isHeading
 */

/**
 * Attributes: data-label-more / data-label-less — button copy per state.
 *
 * Sets `clamped` or `fits` from the measured body copy, `expanded` and `animating` while
 * toggling. With neither `clamped` nor `fits` the stylesheet's own clamp holds, which is
 * the pre-upgrade paint.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} content
 * @property {HTMLElement} button
 * @extends {Component<Refs>}
 */
class ReadMore extends Component {
  requiredRefs = ['content', 'button'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #contentObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #transitionTimeout;

  /** @type {((event: TransitionEvent) => void) | null} */
  #transitionEndHandler = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    requestAnimationFrame(() => this.#measure());

    window.addEventListener('resize', this.#handleResize, {
      signal: this.#abortController.signal,
    });

    // A late web font changes every line height.
    document.fonts?.ready.then(() => this.#measure());

    // Weglot rewrites this text after load on the translated markets.
    this.#contentObserver = new MutationObserver(this.#measureDebounced);
    this.#contentObserver.observe(this.refs.content, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#endTransition();
    this.#measureDebounced.cancel();
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #measureDebounced = debounce(() => this.#measure(), MEASURE_DEBOUNCE);

  #handleResize = () => {
    // Mid-animation the inline max-height is a stale target, so drop it rather than
    // finish a transition toward the old width.
    this.#endTransition();
    this.#measureDebounced();
  };

  /**
   * Range rects ignore overflow clipping, so this reads the same collapsed or expanded and
   * never has to mutate the DOM to measure.
   */
  #measure() {
    if (!this.isConnected || this.hasAttribute(ATTRIBUTES.ANIMATING)) return;

    const { content, button } = this.refs;
    const limit = parseFloat(getComputedStyle(content).getPropertyValue('--line-clamp'));

    if (!limit || limit < 1) return;

    const lines = this.#measureLines();

    // A heading is a title, not body copy: it does not spend the budget, but it still
    // sits above the cut and counts toward the height.
    let body = 0;
    let cut = -1;

    for (let index = 0; index < lines.length; index += 1) {
      if (!lines[index].isHeading) body += 1;

      if (body >= limit) {
        cut = index;
        break;
      }
    }

    const isClamped = cut >= 0 && cut < lines.length - 1;

    // Neither set is the pre-upgrade state, which the stylesheet clamps on its own.
    this.toggleAttribute(ATTRIBUTES.CLAMPED, isClamped);
    this.toggleAttribute(ATTRIBUTES.FITS, !isClamped);

    if (!isClamped) {
      for (const property of Object.values(PROPERTIES)) content.style.removeProperty(property);

      // Widening until the text fits leaves `expanded` meaningless.
      this.removeAttribute(ATTRIBUTES.EXPANDED);
      button.textContent = this.dataset.labelMore ?? '';
      return;
    }

    const contentRect = content.getBoundingClientRect();
    const lastLine = lines[cut];
    const lineHeight = lastLine.bottom - lastLine.top;

    // Leave the last line its descender space, capped so a paragraph margin below it
    // does not add visible slack.
    const gap = Math.max(Math.min((lines[cut + 1].top - lastLine.bottom) / 2, lineHeight / 4), 0);

    content.style.setProperty(
      PROPERTIES.CLAMP_HEIGHT,
      `${Math.ceil(lastLine.bottom + gap - contentRect.top)}px`,
    );
    content.style.setProperty(
      PROPERTIES.LINE_TOP,
      `${Math.floor(lastLine.top - contentRect.top)}px`,
    );
    content.style.setProperty(PROPERTIES.LINE_HEIGHT, `${Math.ceil(lineHeight)}px`);

    // The mask must start on a word boundary or it cuts a glyph in half, so fall back to
    // an earlier word when the last one leaves no room for the dots.
    const reserve = Math.ceil(lineHeight) + 4;
    const fitting = lastLine.rights.filter((right) => right <= contentRect.right - reserve);
    const dotsStart = fitting.length ? Math.max(...fitting) : contentRect.right - reserve;

    content.style.setProperty(
      PROPERTIES.DOTS_START,
      `${Math.ceil(dotsStart - contentRect.left)}px`,
    );
  }

  /**
   * Word rects, not node-wide ones: a node-wide range reports each line's full extent and
   * loses where the last word ends, and a range spanning elements returns their border
   * boxes, merging a paragraph into one line.
   *
   * @returns {Line[]} in document order
   */
  #measureLines() {
    const { content } = this.refs;
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    const range = document.createRange();

    /** @type {{ rect: DOMRect, isHeading: boolean }[]} */
    const rects = [];

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node.nodeValue ?? '';
      if (!text.trim()) continue;

      const heading = node.parentElement?.closest(HEADING_SELECTOR);
      const isHeading = Boolean(heading) && content.contains(heading);

      // /\S+/g matches each run of non-whitespace, so one match per word.
      for (const match of text.matchAll(/\S+/g)) {
        range.setStart(node, match.index);
        range.setEnd(node, match.index + match[0].length);

        for (const rect of range.getClientRects()) {
          if (rect.height > 0) rects.push({ rect, isHeading });
        }
      }
    }

    rects.sort((a, b) => a.rect.top - b.rect.top);

    /** @type {Line[]} */
    const lines = [];

    for (const { rect, isHeading } of rects) {
      const line = lines[lines.length - 1];

      // Rects on one line overlap vertically. 1px absorbs subpixel rounding.
      if (!line || rect.top >= line.bottom - 1) {
        lines.push({ top: rect.top, bottom: rect.bottom, rights: [rect.right], isHeading });
      } else {
        line.top = Math.min(line.top, rect.top);
        line.bottom = Math.max(line.bottom, rect.bottom);
        line.rights.push(rect.right);
      }
    }

    return lines;
  }

  /** Reduced motion and start === end fire no `transitionend`, hence the timeout. */
  #watchTransition() {
    const { content } = this.refs;

    const finish = () => {
      this.#endTransition();
      this.#measure();
    };

    this.#transitionEndHandler = (event) => {
      if (event.propertyName === 'max-height') finish();
    };

    content.addEventListener('transitionend', this.#transitionEndHandler);
    this.#transitionTimeout = setTimeout(finish, TRANSITION_TIMEOUT);
  }

  #endTransition() {
    const { content } = this.refs;

    clearTimeout(this.#transitionTimeout);
    this.#transitionTimeout = undefined;

    if (this.#transitionEndHandler) {
      content.removeEventListener('transitionend', this.#transitionEndHandler);
      this.#transitionEndHandler = null;
    }

    content.style.removeProperty('max-height');
    this.removeAttribute(ATTRIBUTES.ANIMATING);
  }

  /** Reading a layout property forces the pending style change to flush. */
  #flushLayout() {
    return this.refs.content.offsetHeight;
  }

  /** @param {Event} [event] */
  toggle(event) {
    if (event) event.preventDefault();

    const { content, button } = this.refs;

    this.#endTransition();

    const startHeight = content.offsetHeight;
    const isExpanded = !this.hasAttribute(ATTRIBUTES.EXPANDED);

    // No second layout pass needed: scrollHeight stays the full height while clipped.
    const endHeight = isExpanded
      ? content.scrollHeight
      : parseFloat(content.style.getPropertyValue(PROPERTIES.CLAMP_HEIGHT));

    // Commit the current height before arming: arming in the same recalc as a height
    // change makes that change the from-value, which kills the collapse animation.
    if (Number.isFinite(endHeight)) {
      content.style.maxHeight = `${startHeight}px`;
      this.setAttribute(ATTRIBUTES.ANIMATING, '');
      this.#flushLayout();
    }

    this.toggleAttribute(ATTRIBUTES.EXPANDED, isExpanded);
    button.textContent = (isExpanded ? this.dataset.labelLess : this.dataset.labelMore) ?? '';

    if (!Number.isFinite(endHeight)) return;

    content.style.maxHeight = `${endHeight}px`;
    this.#watchTransition();
  }
}

if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}
